<template>
  <div>
    <div
      v-if="recommendId && recommendId !== ''"
      class="mb-2 ai-box">
      <div class="ai-box-content">
        <a @click="clickRecommend(recommendId)">
          <v-icon color="#155e74">mdi-creation</v-icon>
          {{ findItemLabel(recommendId) }}
        </a>
      </div>
    </div>
    <v-menu
      v-model="menu"
      :close-on-content-click="false"
      offset-y
      max-height="348"
      transition="scale-transition">
      <template #activator="{ on, attrs }">
        <v-text-field
          :value="findItemLabel(categoryId)"
          label="Category"
          readonly
          :loading="loading"
          outlined
          dense
          :rules="rules"
          v-bind="attrs"
          prepend-inner-icon="mdi-folder"
          :disabled="disabled"
          v-on="on"
          @click:append="menu = !menu"
        />
      </template>
      <v-card>
        <v-card-text>
          <v-text-field
            v-model="searchText"
            label="Search categories" />
          <v-treeview
            v-model="selected"
            :search="searchText"
            :items="categories"
            activatable
            open-on-click
            item-key="id"
            item-text="localName"
            item-children="children"
            item-disabled="locked"
            @update:active="onSelect"
          />
        </v-card-text>
      </v-card>
    </v-menu>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import humps from 'humps'
import TiktokCategories from '@/assets/json/TiktokCategories.json'
// import TiktokProvider from '@/resources/TiktokProvider.js'

// const TiktokService = new TiktokProvider()

export default {
  props: {
    value: {
      type: String,
      default: ''
    },
    disabled: {
      type: Boolean,
      default: false
    },
    rules: {
      type: Array,
      default: () => []
    },
    recommendId: {
      type: String,
      default: ''
    }
  },
  data () {
    return {
      menu: false,
      selected: [],
      loading: false,
      searchText: '',
      // categories: [],
      rawCategories: humps.camelizeKeys(TiktokCategories)
    }
  },
  computed: {
    categoryId: {
      get () {
        return this.value
      },
      set (val) {
        this.$emit('input', val)
      }
    },
    categories () {
      return this.mapCategory(this.rawCategories)
    }
  },
  methods: {
    ...mapActions({
      setErrorPage: 'Components/setErrorPage',
      setSnackbar: 'Components/setSnackbar'
    }),
    async getCategoryList () {
      try {
        this.loading = true

        this.categories = this.mapCategory(this.rawCategories)
      } catch (error) {
        this.setErrorPage(error.code)
        console.error('getCategoryList', error)
      } finally {
        this.loading = false
      }
    },
    clickRecommend (val) {
      this.categoryId = val
      this.menu = false
    },
    onSelect (val) {
      const item = this.findItem(this.categories, val[0])
      this.categoryId = item ? item.id : ''
      this.menu = false
    },
    findCategory (id) {
      const item = this.findItem(this.categories, id)
      return item ? item.localName : ''
    },
    mapCategory (data) {
      const mapped = {}
      const leafs = []
      const roots = []

      data.forEach((item) => {
        mapped[item.id] = {
          id: item.id,
          localName: item.localName,
          parentNames: [item.localName],
          isLeaf: item.isLeaf,
          parentId: item.parentId,
          permissionStatuses: item.permissionStatuses,
          locked: item.permissionStatuses[0] !== 'AVAILABLE',
          children: []
        }

        if (item.isLeaf) {
          leafs.push(item.id)
        }
      })

      if (this.searchText) {
        leafs.forEach((id) => {
          let parentId = mapped[id]?.parentId || '0'

          while (parentId !== '0') {
            const parent = mapped[parentId]

            if (parent) {
              mapped[id].parentNames.unshift(parent.localName)
            }

            parentId = parent?.parentId || '0'
          }

          mapped[id].localName = mapped[id].parentNames.join(' > ')
          roots.push(mapped[id])
        })
      } else {
        data.forEach((item) => {
          if (item.parentId !== '0' && mapped[item.parentId]) {
            mapped[item.parentId].children.push(mapped[item.id])
          } else if (item.parentId === '0') {
            roots.push(mapped[item.id])
          }
        })
      }

      return roots
    },
    findItemLabel (id) {
      const categoryMap = this.rawCategories.reduce((map, cat) => {
        const temp = map
        temp[cat.id] = cat
        return temp
      }, {})

      const path = []
      let current = categoryMap[+id]
      while (current) {
        path.unshift(current.localName)
        if (current.parentId === '0') break
        current = categoryMap[+current.parentId]
      }

      return path.join(' > ')
    },
    findItem (items, id) {
      for (const item of items) {
        if (item.id === id) return item
        if (item.children) {
          const found = this.findItem(item.children, id)
          if (found) return found
        }
      }
      return null
    }
  }
}
</script>
<style scoped>

.ai-box-content {
  width: 100%;
  border-radius: 8px;
  padding: 8px;
  background-color: white;
}
.ai-box {
  position: relative;
  isolation: isolate;
  padding: 2px;
  border-radius: 10px;
  display: flex;
  justify-content: center;
  align-items: center;
  font-family: sans-serif;
  overflow: hidden;
}

.ai-box::before,
.ai-box::after {
  content: '';
  position: absolute;
  border-radius: inherit;
}

.ai-box::before {
  inset: -2px;
  background: conic-gradient(from var(--glow-deg, 0deg), #3186ff ,#34a853 ,#ffd314 ,#ff4641 ,#3186ff);
  z-index: -1;
}

.ai-box::after {
  inset: -10px;
  background: conic-gradient(from var(--glow-deg, 0deg), #3186ff ,#34a853 ,#ffd314 ,#ff4641 ,#3186ff);
  filter: blur(15px);
  opacity: 0.7;
  z-index: -2;
}
@keyframes rotateGlow {
  to {
    --glow-deg: 360deg;
  }
}

.ai-box::before,
.ai-box::after {
  animation: rotateGlow 3s linear infinite;
}
@property --glow-deg {
  syntax: "<angle>";
  inherits: true;
  initial-value: 0deg;
}
</style>
